import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchPatients } from '../../../store/slices/patientsSlice';
import { fetchNursingOPDAssessment, updateNursingOPDAssessment } from '../../../store/slices/nursingOPDAssessmentsSlice';
import MainLayout from '../../../components/layout/MainLayout';
import Breadcrumbs from '../../../components/common/Breadcrumbs';
import NursingOPDAssessmentForm from './NursingOPDAssessmentForm';
import Swal from 'sweetalert2';
import { getApiErrorMessage } from "../../../utils/apiError";

const EditNursingOPDAssessment = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { id } = useParams();
    const [isLoading, setIsLoading] = useState(false);
    const [initialData, setInitialData] = useState(null);
    const { patients, isLoading: isPatientsLoading } = useSelector((state) => state.patients);
    const { currentNursingOPDAssessment, isLoading: isAssessmentLoading } = useSelector((state) => state.nursingOPDAssessments);

    useEffect(() => {
        dispatch(fetchPatients());
        dispatch(fetchNursingOPDAssessment(id));
    }, [dispatch, id]);

    useEffect(() => {
        if (currentNursingOPDAssessment && currentNursingOPDAssessment.id === Number(id)) {
            // Map flattened info_* fields back to info_given object
            const info_given = {
                admission_process: !!currentNursingOPDAssessment.info_admission_process,
                inform_restrain: !!currentNursingOPDAssessment.info_inform_restrain,
                inform_ecg: !!currentNursingOPDAssessment.info_inform_ecg,
                inform_injection: !!currentNursingOPDAssessment.info_inform_injection,
                uniform_change: !!currentNursingOPDAssessment.info_uniform_change,
                introduce_staff: !!currentNursingOPDAssessment.info_introduce_staff,
                belongings_not_allowed: !!currentNursingOPDAssessment.info_belongings_not_allowed,
                provided_by_karwan: !!currentNursingOPDAssessment.info_provided_by_karwan,
                inform_food: !!currentNursingOPDAssessment.info_inform_food,
                inform_canteen: !!currentNursingOPDAssessment.info_inform_canteen,
            };

            setInitialData({
                ...currentNursingOPDAssessment,
                info_given,
                labs_investigations: currentNursingOPDAssessment.labs || [],
                nursing_plans: currentNursingOPDAssessment.plans || [],
            });
        }
    }, [currentNursingOPDAssessment, id]);

    const handleFormSubmit = async (formData) => {
        setIsLoading(true);
        try {
            await dispatch(updateNursingOPDAssessment({ id, assessmentData: formData })).unwrap();

            Swal.fire({
                title: 'Success!',
                text: 'Nursing Assessment updated successfully!',
                icon: 'success',
                timer: 1500,
                showConfirmButton: false,
                position: 'top',
                width: '300px',
            }).then(() => {
                navigate('/nursing-opd-assessments');
            });
        } catch (error) {
            console.error('Error updating assessment:', error);
            Swal.fire({
                title: 'Error!',
                text: typeof error === 'string' ? error : (getApiErrorMessage(error, 'Failed to update assessment.')),
                icon: 'error',
                confirmButtonText: 'OK',
                position: 'top',
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <MainLayout>
            <div className="container-xxl flex-grow-1 container-p-y">
                <Breadcrumbs />
                <div className="card mb-4 mt-2">
                    <div className="card-header d-flex justify-content-between align-items-center">
                        <h4 className="mb-0"></h4>
                        <div>
                            <button
                                className="btn btn-outline-primary btn-sm"
                                onClick={() => window.open(`/nursing-opd-assessments/print/${id}`, '_blank')}
                            >
                                <i className="ri-printer-line me-1"></i> Print
                            </button>
                        </div>
                    </div>
                    <div className="card-body">
                        {initialData ? (
                            <NursingOPDAssessmentForm
                                initialData={initialData}
                                onSubmit={handleFormSubmit}
                                isLoading={isLoading || isPatientsLoading}
                                buttonText="Update Assessment"
                                patients={patients}
                            />
                        ) : (
                            <div className="d-flex justify-content-center py-5">
                                <div className="spinner-border" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default EditNursingOPDAssessment;
